import { useEffect, useRef, useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { isTourSkipped, skipTours } from './tourUtils';

const TOUR_COMPLETED_KEY = 'stackguard_integration_tour_completed';

let driverInstance = null;

const getTourSteps = () => [
  // Description
  {
    element: '[data-tour="integration-description"]',
    popover: {
      title: 'Identity Management',
      description: 'Connect your favorite cloud services and repositories to unlock powerful security monitoring and automation capabilities. Browse available identity integrations by category.',
      side: 'bottom',
      align: 'start'
    }
  },
  // Category Groups
  {
    element: '[data-tour="integration-categories"]',
    popover: {
      title: 'Integration Categories',
      description: 'Integrations are organized into categories: Cloud Infrastructure, Storage, Version Control, Communication Services, Service Accounts, CI/CD Tools, Container Registries, and SaaS Applications. Each category contains relevant integrations.',
      side: 'top',
      align: 'start'
    }
  },
  // Cloud Infrastructure Category
  {
    element: '[data-tour="integration-cloud-infra"]',
    popover: {
      title: 'Cloud Infrastructure',
      description: 'Connect cloud providers like AWS, Azure, and GCP for IAM scanning. Note: Some providers may require configuration in Manage Scans before they become available.',
      side: 'right',
      align: 'start'
    }
  },
  // Storage Category
  {
    element: '[data-tour="integration-storage"]',
    popover: {
      title: 'Cloud Storage & Buckets',
      description: 'Integrate with cloud storage services like S3, Azure Blob, and Google Cloud Storage to scan for exposed secrets and misconfigurations.',
      side: 'right',
      align: 'start'
    }
  },
  // Version Control Category
  {
    element: '[data-tour="integration-vcs"]',
    popover: {
      title: 'Version Control System',
      description: 'Connect your repositories from GitHub, GitLab, Bitbucket, and other version control systems to scan for secrets and security issues.',
      side: 'right',
      align: 'start'
    }
  },
  // Communication Services Category
  {
    element: '[data-tour="integration-communication"]',
    popover: {
      title: 'Chat Applications',
      description: 'Integrate with communication tools like Slack, Teams, and Webex to receive security alerts and notifications.',
      side: 'right',
      align: 'start'
    }
  },
  // Integration Cards
  {
    element: '[data-tour="integration-cards"]',
    popover: {
      title: 'Integration Cards',
      description: 'Click on any integration card to connect and configure it. Disabled integrations (grayed out) require configuration in Manage Scans first. Hover over cards to see more details.',
      side: 'left',
      align: 'start'
    }
  },
  // Tour Complete
  {
    popover: {
      title: 'Tour Complete!',
      description: 'You\'ve completed the Identity Management tour! You can always restart it by clicking the \"Start Tour\" button. Start connecting your integrations to enhance your security monitoring!'
    }
  }
];

// Global click handler for driver buttons
let globalDriverClickHandler = null;

// Function to add skip button to driver popover
const addSkipButtonToPopover = (instance) => {
  // Find the driver popover footer where buttons are located
  const popover = document.querySelector('.driver-popover');
  if (!popover) return;

  // Find the footer element that contains the buttons
  const footer = popover.querySelector('.driver-popover-footer');
  if (!footer) return;

  // Check if skip button already exists and remove it if on last step
  const existingSkipButton = popover.querySelector('.driver-skip-button');
  if (existingSkipButton) {
    // Remove existing skip button if we're on last step
    const progressIndicatorCheck = footer.querySelector('.driver-popover-progress-text');
    if (progressIndicatorCheck) {
      const progressText = progressIndicatorCheck.textContent || '';
      const match = progressText.match(/(\d+)\s+of\s+(\d+)/i);
      if (match) {
        const currentStep = parseInt(match[1], 10);
        const totalSteps = parseInt(match[2], 10);
        if (currentStep === totalSteps) {
          existingSkipButton.remove();
        }
      }
    }
    // If skip button exists and we're not on last step, don't recreate
    if (existingSkipButton.parentNode) {
      return;
    }
  }

  // Find all elements in the footer
  const previousButton = Array.from(footer.querySelectorAll('button')).find(btn => {
    const text = btn.textContent?.toLowerCase().trim() || '';
    return text.includes('previous') || text.includes('prev') || btn.classList.contains('driver-prev-btn');
  });
  
  const nextButton = Array.from(footer.querySelectorAll('button')).find(btn => {
    const text = btn.textContent?.toLowerCase().trim() || '';
    return text.includes('next') || btn.classList.contains('driver-next-btn');
  });

  const progressIndicator = footer.querySelector('.driver-popover-progress-text');

  // Check if we're on the last step by parsing progress indicator
  let isLastStep = false;
  if (progressIndicator) {
    const progressText = progressIndicator.textContent || '';
    const match = progressText.match(/(\d+)\s+of\s+(\d+)/i);
    if (match) {
      const currentStep = parseInt(match[1], 10);
      const totalSteps = parseInt(match[2], 10);
      isLastStep = currentStep === totalSteps;
    }
  }

  // Get reference button to match styling
  const referenceButton = previousButton || nextButton;
  if (!referenceButton) return;

  // Only create skip button if not on last step
  let skipButton = null;
  if (!isLastStep) {
    // Get computed styles from reference button to match styling exactly
    const refStyles = window.getComputedStyle(referenceButton);
    
    // Get all relevant styles to ensure perfect consistency
    const buttonStyles = {
      padding: refStyles.padding,
      height: refStyles.height,
      minHeight: refStyles.minHeight,
      background: refStyles.backgroundColor,
      border: refStyles.border,
      borderWidth: refStyles.borderWidth,
      borderStyle: refStyles.borderStyle,
      borderColor: refStyles.borderColor,
      color: refStyles.color,
      borderRadius: refStyles.borderRadius,
      cursor: refStyles.cursor,
      fontSize: refStyles.fontSize,
      fontWeight: refStyles.fontWeight,
      fontFamily: refStyles.fontFamily,
      lineHeight: refStyles.lineHeight,
      display: refStyles.display,
      alignItems: refStyles.alignItems,
      justifyContent: refStyles.justifyContent,
      whiteSpace: refStyles.whiteSpace,
      margin: refStyles.margin,
      boxSizing: refStyles.boxSizing,
    };

    // Create skip button with exact same styling as other buttons
    skipButton = document.createElement('button');
    skipButton.className = 'driver-skip-button';
    skipButton.textContent = 'Skip Tour';
    skipButton.setAttribute('aria-label', 'Skip tour');
    
    // Apply all styles to match exactly
    Object.assign(skipButton.style, {
      padding: buttonStyles.padding,
      height: buttonStyles.height,
      minHeight: buttonStyles.minHeight,
      background: buttonStyles.background,
      border: buttonStyles.border,
      color: buttonStyles.color,
      borderRadius: buttonStyles.borderRadius,
      cursor: buttonStyles.cursor,
      fontSize: buttonStyles.fontSize,
      fontWeight: buttonStyles.fontWeight,
      fontFamily: buttonStyles.fontFamily,
      lineHeight: buttonStyles.lineHeight,
      display: buttonStyles.display || 'inline-flex',
      alignItems: buttonStyles.alignItems || 'center',
      justifyContent: buttonStyles.justifyContent || 'center',
      whiteSpace: buttonStyles.whiteSpace || 'nowrap',
      margin: buttonStyles.margin || '0',
      boxSizing: buttonStyles.boxSizing || 'border-box',
      transition: 'all 0.2s',
    });

    // Add hover effect matching other buttons exactly
    skipButton.addEventListener('mouseenter', () => {
      // Check if reference button has a hover state we can observe
      const hoverBg = refStyles.getPropertyValue('--hover-background') || 
                      (referenceButton.onmouseenter ? '#f3f4f6' : buttonStyles.background);
      skipButton.style.background = hoverBg;
    });
    skipButton.addEventListener('mouseleave', () => {
      skipButton.style.background = buttonStyles.background;
    });

    // Handle skip button click
    skipButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      skipTours();
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
  }

  // Restructure footer with flex layout: Previous | Progress | [Skip Tour, Next]
  // Set footer to flex with justify-between
  footer.style.cssText = `
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 8px;
    min-height: 40px;
  `;

  // Create left container for Previous button
  const leftContainer = document.createElement('div');
  leftContainer.style.cssText = 'display: flex; align-items: center; flex-shrink: 0;';
  if (previousButton) {
    leftContainer.appendChild(previousButton);
  }

  // Create center container for Progress indicator
  const centerContainer = document.createElement('div');
  centerContainer.style.cssText = 'display: flex; align-items: center; flex: 1; justify-content: center; min-width: 0;';
  if (progressIndicator) {
    // Ensure progress indicator displays on one line
    progressIndicator.style.whiteSpace = 'nowrap';
    centerContainer.appendChild(progressIndicator);
  }

  // Create right container for Skip Tour and Next buttons
  const rightContainer = document.createElement('div');
  rightContainer.style.cssText = 'display: flex; align-items: center; gap: 8px; flex-shrink: 0;';
  
  // Only add skip button if not on last step
  if (skipButton) {
    rightContainer.appendChild(skipButton);
  }
  
  if (nextButton) {
    rightContainer.appendChild(nextButton);
  }

  // Clear footer and add new structure
  footer.innerHTML = '';
  footer.appendChild(leftContainer);
  footer.appendChild(centerContainer);
  footer.appendChild(rightContainer);
};

const createDriverInstance = () => {
  // Remove existing global handler if any
  if (globalDriverClickHandler) {
    document.removeEventListener('click', globalDriverClickHandler, true);
    globalDriverClickHandler = null;
  }
  
  // Always create a fresh instance
  const instance = driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    allowClose: true,
    overlayOpacity: 0.5,
    steps: getTourSteps(),
    onDestroyStarted: (element, step, options) => {
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
    },
    onDestroyed: (element, step, options) => {
      if (globalDriverClickHandler) {
        document.removeEventListener('click', globalDriverClickHandler, true);
        globalDriverClickHandler = null;
      }
      // Clean up after tour is destroyed
      if (driverInstance === instance) {
        driverInstance = null;
      }
    },
    onHighlightStarted: (element, step, options) => {
      // Add skip button to popover when tour starts highlighting
      setTimeout(() => addSkipButtonToPopover(instance), 50);
    },
    onHighlighted: (element, step, options) => {
      // Ensure skip button is added to popover
      setTimeout(() => addSkipButtonToPopover(instance), 50);
    },
    onStarted: () => {
      // Add skip button when tour starts
      setTimeout(() => addSkipButtonToPopover(instance), 50);
    }
  });
  
  // Set up global click handler for driver buttons
  globalDriverClickHandler = (e) => {
    const target = e.target;
    const button = target.closest('button');
    
    // Check if click is on skip button
    if (button && button.classList.contains('driver-skip-button')) {
      // Skip button handles its own click, but we can prevent default behavior
      return;
    }
    
    // Only handle clicks inside driver popover
    const popover = target.closest('.driver-popover');
    if (!popover) return;
    
    if (button) {
      const buttonText = button.textContent?.toLowerCase().trim() || '';
      const isDoneButton = buttonText.includes('done') || 
                          buttonText.includes('finish') ||
                          button.getAttribute('data-driver-done-button') !== null;
      const isCloseButton = buttonText.includes('×') || 
                           buttonText.includes('✕') ||
                           button.getAttribute('data-driver-close-button') !== null ||
                           button.classList.contains('driver-close-btn') ||
                           button.classList.contains('driver-popover-close-btn') ||
                           button.closest('.driver-popover-close-btn') !== null;
      
      if (isDoneButton || isCloseButton) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (instance && typeof instance.destroy === 'function') {
          instance.destroy();
        }
        return false;
      }
    }
  };
  
  // Add global click handler in capture phase to catch before driver.js handlers
  document.addEventListener('click', globalDriverClickHandler, true);
  
  return instance;
};

const initializeDriver = () => {
  // If instance exists and is active, destroy it first
  if (driverInstance) {
    try {
      driverInstance.destroy();
    } catch (e) {
      // Ignore errors if already destroyed
    }
    driverInstance = null;
  }

  // Create a fresh instance
  driverInstance = createDriverInstance();
  return driverInstance;
};

export const useIntegrationTour = (autoStart = false) => {
  const driverObjRef = useRef(null);

  useEffect(() => {
    // Auto-start tour for first-time users
    if (autoStart) {
      const hasCompletedTour = localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
      const toursSkipped = isTourSkipped();
      if (!hasCompletedTour && !toursSkipped) {
        // Initialize driver.js
        const driverObj = initializeDriver();
        driverObjRef.current = driverObj;
        
        // Wait for DOM to be ready
        setTimeout(() => {
          driverObj.drive();
        }, 1000);
      }
    }

    return () => {
      // Don't destroy on unmount, just clear ref
      driverObjRef.current = null;
    };
  }, [autoStart]);

  const startTour = useCallback(() => {
    // Initialize and start tour with a fresh instance
    const driverObj = initializeDriver();
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      driverObj.drive();
    }, 100);
  }, []);

  return { startTour };
};

export default useIntegrationTour;

